/**
 * Sprint remote-ops F : heartbeat périodique du device vers Supabase.
 *
 * Appelé une fois au mount de l'AppLayout. Envoie un ping toutes les 5 minutes
 * avec l'ID device, le modèle Sunmi, la version APK, le SDK Android. Permet
 * au super_admin / revendeur de voir dans AdminPortal :
 *   - qui est en ligne en temps réel
 *   - quelle version APK tourne sur quelle tablette
 *   - depuis quand un device n'est plus remonté (alerte support)
 *
 * Le RPC `device_heartbeat` est SECURITY DEFINER et upsert scopé au tenant
 * de l'utilisateur connecté (via get_my_tenant_id). Pas de tenant_id à
 * fournir côté client — la policy RLS bloque tout bypass.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { detectDeviceContext } from '@/lib/remote-commands/device-context';
import { DeviceRemoteApi } from '@/api/devices/DeviceRemoteApi';
import { applyAutoUpdateLock } from '@/lib/remote-commands/update-lock-sync';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const HEARTBEAT_STORAGE_KEY = 'visionpos.device.id';

export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(HEARTBEAT_STORAGE_KEY);
    if (existing) return existing;
  } catch {
    /* storage blocked */
  }
  const fresh = 'dev_' + crypto.randomUUID();
  try {
    localStorage.setItem(HEARTBEAT_STORAGE_KEY, fresh);
  } catch {
    /* ignore */
  }
  return fresh;
}

function readLocalConfig() {
  // Lecture sans throw — localStorage peut être bloqué (privacy / iframe)
  const safeGet = (k: string): string | null => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  const kiosk = safeGet('vp_kiosk_mode'); // useKioskMode.ts:STORAGE_KEY
  const nativeFlag =
    (window as Window & { __VISIONPOS_ANDROID?: boolean }).__VISIONPOS_ANDROID === true;
  const installed =
    nativeFlag ||
    (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches);
  return {
    kiosk_mode: kiosk && kiosk !== 'disabled' ? kiosk : null,
    is_native: nativeFlag,
    is_pwa_standalone: !!(
      typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches
    ),
    is_installed: installed,
    online: navigator.onLine,
    screen:
      typeof window !== 'undefined'
        ? { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 }
        : null,
    locale: navigator.language || null,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    updater: readLastUpdaterEvent(),
  };
}

const UPDATER_EVENT_KEY = 'vp_updater_last_event';

type UpdaterWindow = Parameters<typeof applyAutoUpdateLock>[0];

/**
 * Dernière étape de mise à jour vue par ce terminal (événements `vp-updater` de l'APK) :
 * sans elle, le back-office ne sait pas pourquoi une caisse reste sur une ancienne version.
 */
function rememberUpdaterEvent(event: Event): void {
  const { type, reason } = ((event as CustomEvent).detail ?? {}) as {
    type?: string;
    reason?: string | number;
  };
  if (!type || type === 'download-progress') return;
  try {
    localStorage.setItem(
      UPDATER_EVENT_KEY,
      JSON.stringify({ type, reason: reason ?? null, at: new Date().toISOString() }),
    );
  } catch {
    /* stockage indisponible : on perd seulement ce diagnostic */
  }
}

type UpdaterEvent = { type: string; reason: string | number | null; at: string };

function readLastUpdaterEvent(): UpdaterEvent | null {
  try {
    return JSON.parse(localStorage.getItem(UPDATER_EVENT_KEY) ?? 'null') as UpdaterEvent | null;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') window.addEventListener('vp-updater', rememberUpdaterEvent);

export function useDeviceHeartbeat(opts: { enabled?: boolean } = {}) {
  const { enabled = true } = opts;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCallRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const deviceId = getOrCreateDeviceId();

    const ping = async () => {
      // Dedupe rapid successive calls (ex: StrictMode remount)
      const now = Date.now();
      if (now - lastCallRef.current < 30_000) return;
      lastCallRef.current = now;

      try {
        // Skip if user not authenticated — no tenant context
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session) return;

        // Récupère le home_screen depuis profiles (best-effort, ne bloque pas le ping)
        let homeScreen: string | null = null;
        try {
          const userId = session.session.user?.id;
          if (userId) {
            const { data: prof } = await supabase
              .from('profiles')
              .select('home_screen')
              .eq('user_id', userId)
              .maybeSingle();
            homeScreen = (prof as { home_screen?: string | null } | null)?.home_screen ?? null;
          }
        } catch {
          /* silencieux */
        }

        const local = readLocalConfig();
        // Relu à chaque battement : les drapeaux natifs arrivent après le montage.
        const ctx = detectDeviceContext(window, navigator.userAgent || '', navigator.platform);
        await supabase.rpc('device_heartbeat', {
          p_device_id: deviceId,
          p_device_name: ctx.deviceName,
          p_model: ctx.model,
          p_manufacturer: ctx.manufacturer,
          p_app_version: ctx.appVersion,
          p_version_code: ctx.versionCode,
          p_android_sdk: ctx.androidSdk,
          p_os_version: ctx.osVersion,
          p_metadata: {
            ua: navigator.userAgent.slice(0, 256),
            kiosk_mode: local.kiosk_mode,
            home_screen: homeScreen,
            is_native: local.is_native || ctx.isNative,
            remote_ops: ctx.remoteOps,
            root: ctx.root,
            is_pwa_standalone: local.is_pwa_standalone,
            is_installed: local.is_installed,
            online: local.online,
            screen: local.screen,
            locale: local.locale,
            tz: local.tz,
            pathname: local.pathname,
            updater: local.updater,
            heartbeat_at: new Date().toISOString(),
          },
        });
        // Kill switch « updates.autoupdate » transmis à l'APK (sa vérification native
        // toutes les 30 min lisait version.json sans le consulter).
        if (ctx.isNative) {
          const locked = await DeviceRemoteApi.isAutoUpdateLocked();
          if (locked.ok) applyAutoUpdateLock(window as UpdaterWindow, locked.value);
        }
      } catch (err) {
        // Silencieux — un heartbeat raté n'est pas critique
        if (import.meta.env.DEV) console.warn('[Heartbeat] failed', err);
      }
    };

    // Initial ping slightly delayed to let auth settle
    const initial = setTimeout(ping, 3000);
    intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearTimeout(initial);
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
